import React, { Suspense } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { Breadcrumbs } from './styled'
import Link from '../Link'
import { wrapDocument } from '../../services/data'
import useUser from '../../services/useUser'

const documents = {}
const getDocument = path =>
  documents[path] || (documents[path] = wrapDocument(path))

/**
 * Build Firestore document path from route path
 * @param {string} to - The route path (e.g., '/employers/1/calculations/5')
 * @param {string} employerId - The current employer ID from URL params
 * @returns {string} Firestore path (e.g., 'employers/1/calculations/5')
 */
function buildFirestorePath(to, employerId) {
  // Handle new dynamic employer routes: /employers/:id/... -> employers/:id/...
  if (to.startsWith('/employers/')) {
    return to.replace(/^\//, '')
  }
  // Legacy routes - prepend employers/{employerId}
  return `employers/${employerId || 1}${to}`
}

function Crumb({ to, label, active, employerId, originalId }) {
  if (typeof(label) === 'function') {
    // Use originalId if provided, otherwise extract from path
    const id = originalId || to.split('/').pop()
    const path = buildFirestorePath(to, employerId)
    
    try {
      const docData = getDocument(path).read().data() || {}
      label = label({ id, ...docData })
    } catch (e) {
      // Let Suspense handle pending document reads.
      if (e && typeof e.then === 'function') {
        throw e
      }

      // Document might not exist or failed to load.
      label = label({ id })
    }
  }

  // Skip rendering crumb if no label
  if (!label) {
    return null
  }

  return (
    <Link color="inherit" to={to} key={to} active={active}>
      {label}
    </Link>
  )
}

export default function AppBreadcrumbs({ nameMap }) {
  const { pathname } = useLocation()
  const { employerId } = useParams()
  const user = useUser()
  const role = user?.customClaims?.role
  // Admin and payroll accountant both browse the employers list / employer
  // pages, so they keep the employer-related breadcrumbs.
  const showsEmployerCrumbs = role === 'admin' || role === 'payrollAccountant'
  const pathnames = pathname.split('/').filter(x => x)

  return (
    <Suspense fallback="...">
      <Breadcrumbs separator="›" aria-label="breadcrumb">
        {pathnames.map((value, index) => {
          const last = index === pathnames.length - 1
          let to = `/${pathnames.slice(0, index + 1).join('/')}`
          let originalId = null // Used for label when path is modified
          
          // Redirect /employers/:id to /employers/:id (no change needed)
          // Skip the old /employer redirect logic as we're now using /employers/
          
          // Hide employer-related breadcrumbs for users who don't browse them
          // Skip "מעסיקים" (employers list) and employer name crumbs
          if (!showsEmployerCrumbs) {
            // Skip /employers (employers list)
            if (to === '/employers') {
              return null
            }
            // Skip /employers/:id (specific employer)
            if (to.match(/^\/employers\/\d+$/) || to.match(/^\/employers\/[a-z]+$/)) {
              return null
            }
          }
          
          // Build the label key for nameMap lookup
          // For path like /employers/1/calculations/5
          // index 0: value='employers' -> key='employers'
          // index 1: value='1' -> key='employers/*'
          // index 2: value='calculations' -> key='employers/*/calculations'
          // index 3: value='5' -> key='employers/*/calculations/*'
          const labelKey = pathnames.slice(0, index + 1)
            .map((val, idx) => idx % 2 === 0 ? val : '*')
            .join('/')
          
          const label = nameMap[labelKey] || nameMap[value]

          return (
            <Crumb key={to} label={label} to={to} active={!last} employerId={employerId} originalId={originalId} />
          )
        })}
      </Breadcrumbs>
    </Suspense>
  )
}